/**
 * Inventario de muebles en ficha propiedad: Ver (solo lectura) / Editar (checklist PWA).
 */
import { useCallback, useEffect, useState } from "react";
import { useTheme } from "../ThemeProvider";
import api from "../../services/api";
import {
  ensureInventarioMueblesShape,
  inventarioMueblesTieneLineas,
  newInventarioItemId,
} from "../../utils/inventarioMueblesShape";

/**
 * @param {"ver"|"editar"} modo
 * @param {object|null} inventarioInicial
 * @param {string|null} propiedadId
 * @param {function} [onClose]
 * @param {function} [onSaved] — (inventario) => void
 * @param {boolean} [puedeEditar]
 */
export default function InventarioMueblesFicha({
  modo = "ver",
  inventarioInicial = null,
  propiedadId = null,
  onClose,
  onSaved,
  puedeEditar = true,
}) {
  const { theme } = useTheme();
  const c = theme.colors;
  const isDark = theme.name === "dark";
  const editable = modo === "editar" && puedeEditar;

  const [inventario, setInventario] = useState(() => ensureInventarioMueblesShape(inventarioInicial));
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");

  const sig = inventarioInicial ? JSON.stringify(inventarioInicial) : "";
  useEffect(() => {
    setInventario(ensureInventarioMueblesShape(inventarioInicial));
    setError("");
    setOkMsg("");
  }, [sig, modo]);

  const toggleMarcado = (estId, itemId) => {
    if (!editable) return;
    setInventario((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        estancias: prev.estancias.map((est) =>
          est.id !== estId
            ? est
            : {
                ...est,
                items: est.items.map((it) =>
                  it.id !== itemId ? it : { ...it, marcado: !it.marcado },
                ),
              },
        ),
      };
    });
  };

  const setTextoItem = (estId, itemId, texto) => {
    if (!editable) return;
    setInventario((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        estancias: prev.estancias.map((est) =>
          est.id !== estId
            ? est
            : {
                ...est,
                items: est.items.map((it) =>
                  it.id !== itemId ? it : { ...it, texto },
                ),
              },
        ),
      };
    });
  };

  const addLineaVacia = (estId) => {
    if (!editable) return;
    setInventario((prev) => {
      const base = prev || { version: 1, generado_en: null, estancias: [] };
      return {
        ...base,
        estancias: base.estancias.map((est) =>
          est.id !== estId
            ? est
            : {
                ...est,
                items: [
                  ...est.items,
                  { id: newInventarioItemId(), texto: "", marcado: true, esManual: true },
                ],
              },
        ),
      };
    });
  };

  const quitarItem = (estId, itemId) => {
    if (!editable) return;
    setInventario((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        estancias: prev.estancias.map((est) =>
          est.id !== estId
            ? est
            : { ...est, items: est.items.filter((it) => it.id !== itemId) },
        ),
      };
    });
  };

  const renombrarEstancia = (estId, nombre) => {
    if (!editable) return;
    setInventario((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        estancias: prev.estancias.map((est) =>
          est.id !== estId ? est : { ...est, nombre },
        ),
      };
    });
  };

  const guardar = useCallback(async () => {
    if (!propiedadId || !editable) return;
    const shaped = ensureInventarioMueblesShape(inventario);
    if (!shaped) {
      setError("No hay inventario para guardar");
      return;
    }
    setGuardando(true);
    setError("");
    setOkMsg("");
    try {
      const res = await api.patch(`/propiedades/${propiedadId}/inventario-muebles`, {
        inventario_muebles: shaped,
      });
      const saved = ensureInventarioMueblesShape(res.data?.inventario_muebles) || shaped;
      setInventario(saved);
      setOkMsg("Inventario guardado. Genera un nuevo encargo en Documentación para que el propietario lo firme.");
      onSaved?.(saved);
    } catch (e) {
      setError(e.response?.data?.error || "Error al guardar el inventario");
    } finally {
      setGuardando(false);
    }
  }, [propiedadId, editable, inventario, onSaved]);

  const boxStyle = {
    marginTop: 12,
    padding: "14px 16px",
    borderRadius: 10,
    border: `1px solid ${isDark ? "rgba(139,92,246,0.35)" : "#e9d5ff"}`,
    background: isDark ? "rgba(139,92,246,0.08)" : "#faf5ff",
  };

  const vacio = !inventarioMueblesTieneLineas(inventario);

  return (
    <div style={boxStyle}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
        <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.06em", color: "#8b5cf6" }}>
          Inventario de muebles · {editable ? "Editar" : "Ver"}
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            style={{
              border: "none",
              background: "transparent",
              color: c.textMuted,
              cursor: "pointer",
              fontSize: 13,
              fontWeight: 600,
              fontFamily: "inherit",
            }}
          >
            Cerrar
          </button>
        )}
      </div>

      {editable && (
        <p style={{ margin: "0 0 12px", fontSize: 11, color: c.textMuted, lineHeight: 1.45 }}>
          Misma checklist que en la app: por estancias, con líneas marcadas y desmarcadas.
          Tras guardar, genera un nuevo encargo en Documentación para firmar el inventario actualizado.
        </p>
      )}

      {error && (
        <div style={{ marginBottom: 10, padding: "8px 10px", borderRadius: 8, background: "rgba(239,68,68,0.12)", color: "#ef4444", fontSize: 12 }}>
          {error}
        </div>
      )}
      {okMsg && (
        <div style={{ marginBottom: 10, padding: "8px 10px", borderRadius: 8, background: "rgba(34,197,94,0.12)", color: "#16a34a", fontSize: 12 }}>
          {okMsg}
        </div>
      )}

      {vacio && !editable && (
        <p style={{ margin: 0, fontSize: 12, color: c.textMuted }}>
          No hay inventario de muebles generado en la PWA para esta ficha.
        </p>
      )}

      {vacio && editable && (
        <div style={{ marginBottom: 12 }}>
          <p style={{ margin: "0 0 8px", fontSize: 12, color: c.textMuted }}>
            No hay inventario aún. Puedes crear estancias y líneas a mano.
          </p>
          <button
            type="button"
            onClick={() =>
              setInventario({
                version: 1,
                generado_en: null,
                estancias: [{ id: newInventarioItemId(), nombre: "Estancia 1", items: [] }],
              })
            }
            style={{
              padding: "6px 12px",
              borderRadius: 7,
              border: `1px solid ${c.border}`,
              background: c.bgInput,
              color: c.textMuted,
              fontSize: 11,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            Empezar inventario manual
          </button>
        </div>
      )}

      {inventario && inventario.estancias.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {inventario.estancias.map((est) => (
            <div
              key={est.id}
              style={{
                padding: 12,
                borderRadius: 9,
                background: c.bgInput,
                border: `1px solid ${c.border}`,
              }}
            >
              {editable ? (
                <input
                  type="text"
                  value={est.nombre}
                  onChange={(e) => renombrarEstancia(est.id, e.target.value)}
                  placeholder="Nombre de la estancia"
                  style={{
                    width: "100%",
                    marginBottom: 10,
                    padding: "6px 10px",
                    fontSize: 14,
                    fontWeight: 700,
                    border: `1px dashed ${c.border}`,
                    borderRadius: 7,
                    background: c.bgCard,
                    color: c.textPrimary,
                    fontFamily: "inherit",
                  }}
                />
              ) : (
                <div style={{ marginBottom: 10, fontSize: 14, fontWeight: 700, color: c.textPrimary }}>
                  {est.nombre}
                </div>
              )}
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {est.items.map((it) => (
                  <div key={it.id} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <input
                      type="checkbox"
                      checked={!!it.marcado}
                      disabled={!editable}
                      onChange={() => toggleMarcado(est.id, it.id)}
                      style={{
                        width: 16,
                        height: 16,
                        accentColor: "#8b5cf6",
                        flexShrink: 0,
                        cursor: editable ? "pointer" : "default",
                        opacity: it.marcado ? 1 : 0.55,
                      }}
                    />
                    {editable ? (
                      <input
                        type="text"
                        value={it.texto}
                        onChange={(e) => setTextoItem(est.id, it.id, e.target.value)}
                        placeholder="Descripción del ítem"
                        style={{
                          flex: 1,
                          padding: "7px 10px",
                          fontSize: 13,
                          border: `1px solid ${c.border}`,
                          borderRadius: 7,
                          background: c.bgCard,
                          color: c.textPrimary,
                          fontFamily: "inherit",
                          textDecoration: it.marcado ? "none" : "line-through",
                          opacity: it.marcado ? 1 : 0.7,
                        }}
                      />
                    ) : (
                      <span
                        style={{
                          flex: 1,
                          fontSize: 13,
                          color: c.textPrimary,
                          textDecoration: it.marcado ? "none" : "line-through",
                          opacity: it.marcado ? 1 : 0.65,
                        }}
                      >
                        {it.texto || "—"}
                      </span>
                    )}
                    {editable && (
                      <button
                        type="button"
                        onClick={() => quitarItem(est.id, it.id)}
                        title="Quitar línea"
                        style={{
                          flexShrink: 0,
                          width: 28,
                          height: 28,
                          borderRadius: 6,
                          border: `1px solid ${c.border}`,
                          background: c.bgCard,
                          color: c.textMuted,
                          cursor: "pointer",
                          fontSize: 14,
                        }}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {editable && (
                <button
                  type="button"
                  onClick={() => addLineaVacia(est.id)}
                  style={{
                    marginTop: 10,
                    padding: "6px 12px",
                    borderRadius: 7,
                    border: `1px dashed ${c.border}`,
                    background: "transparent",
                    color: c.textMuted,
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: "pointer",
                    fontFamily: "inherit",
                  }}
                >
                  + Añadir línea en blanco
                </button>
              )}
            </div>
          ))}

          {editable && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
              <button
                type="button"
                disabled={guardando || !propiedadId}
                onClick={guardar}
                style={{
                  padding: "9px 18px",
                  borderRadius: 8,
                  border: "none",
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: guardando || !propiedadId ? "not-allowed" : "pointer",
                  fontFamily: "inherit",
                  background: guardando || !propiedadId ? c.border : "#8b5cf6",
                  color: "#fff",
                }}
              >
                {guardando ? "Guardando…" : "Guardar inventario"}
              </button>
              <button
                type="button"
                onClick={() =>
                  setInventario((prev) => ({
                    ...(prev || { version: 1, generado_en: null }),
                    estancias: [
                      ...(prev?.estancias || []),
                      {
                        id: newInventarioItemId(),
                        nombre: `Estancia ${(prev?.estancias?.length || 0) + 1}`,
                        items: [],
                      },
                    ],
                  }))
                }
                style={{
                  padding: "9px 14px",
                  borderRadius: 8,
                  border: `1px solid ${c.border}`,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  background: c.bgCard,
                  color: c.textMuted,
                }}
              >
                + Nueva estancia
              </button>
              {!propiedadId && (
                <span style={{ fontSize: 11, color: "#f59e0b" }}>
                  Guarda la propiedad antes de editar el inventario.
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
